import type { UpdateProfileBody } from "@nlc/shared-types";

import { zodResolver } from "@hookform/resolvers/zod";
import { updateProfileBodyZod } from "@nlc/shared-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/button";
import { Loader } from "@/components/Loader";
import { toast } from "@/components/toast/toast-queue";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

import { FeaturePageLayout } from "../shared";
import { useAvatarUpload } from "../users/shared/hooks/useAvatarUpload";
import { ProfileTabsContainer } from "./components/ProfileTabsContainer";
import UpdatePasswordProfilePage from "./components/UpdatePassword";
import { useProfileTabs } from "./hooks/useProfileTabs";

export default function ProfilePage() {
  const { data: userData, isLoading } = useUser();
  const user = userData?.user;
  const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);

  const tabs = useProfileTabs(user?.roleId);

  const { control, handleSubmit: hookFormSubmit } = useForm<UpdateProfileBody>({
    resolver: zodResolver(updateProfileBodyZod),
    values: {
      city: user?.city || "",
      country: user?.country || "",
      customerSuccessId: user?.customerSuccessId || undefined,
      designationId: user?.designationId || undefined,
      email: user?.email || "",
      initials: user?.initials || "",
      name: user?.name || "",
      phoneNumber: user?.phoneNumber || "",
      state: user?.state || "",
      timezoneId: user?.timezoneId || undefined,
    },
  });

  const queryClient = useQueryClient();
  const { mutate: updateProfile } = useMutation({
    mutationFn: (data: UpdateProfileBody) =>
      apiClient.url(`/users/me`).put(data).res(),
    onError: () => {
      toast.add({
        description: "Failed to update profile",
        timeout: 3000,
        type: "error",
      });
    },
    onSuccess: () => {
      toast.add({
        description: "Profile updated successfully",
        timeout: 3000,
        type: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });

  const avatarUpload = useAvatarUpload();

  const openModal = () => setPasswordModalOpen(true);
  const closeModal = () => setPasswordModalOpen(false);
  const handleSubmit = hookFormSubmit((data) => updateProfile(data));

  return (
    <div className="p-xl">
      <FeaturePageLayout
        headerContent={
          <div className="flex flex-row justify-end items-end w-full">
            <Button onPress={openModal} variant="secondary">
              <Lock />
              Update password
            </Button>
          </div>
        }
        title="Edit Profile"
      >
        {isLoading ? (
          <div className="bg-bg-primary p-5">
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-text-tertiary">
                <Loader className="bg-bg-primary" />
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            <form onSubmit={handleSubmit}>
              <div className="bg-bg-primary rounded-b-lg">
                <ProfileTabsContainer
                  avatarUpload={avatarUpload}
                  control={control}
                  initials={user?.initials}
                  projects={userData?.projects?.map((project) => ({
                    id: project.id,
                    name: project.name,
                  }))}
                  roleDisplayName={user?.roleDisplayName}
                  roleId={user?.roleId}
                  tabs={tabs}
                  teamName={user?.teamName}
                  userAvatar={user?.avatar}
                  userId={user?.id}
                  userName={user?.name}
                />
              </div>
            </form>
            <UpdatePasswordProfilePage
              isOpen={isPasswordModalOpen}
              onClose={closeModal}
            />
          </div>
        )}
      </FeaturePageLayout>
    </div>
  );
}
